/**
 * RTWO Asset & Token Engine — Adapter Registries
 */

import type {
  CryptographyAdapterStatus,
  ICryptographyAdapter,
  ICryptographyAdapterRegistry,
} from '../adapters/cryptography-adapter-interface';
import { NullCryptographyAdapter } from '../adapters/null-cryptography-adapter';
import type {
  EventBusAdapterStatus,
  IEventBusAdapter,
  IEventBusAdapterRegistry,
} from '../adapters/event-bus-adapter-interface';
import { NullEventBusAdapter } from '../adapters/null-event-bus-adapter';
import type {
  ILedgerAdapter,
  ILedgerAdapterRegistry,
  LedgerAdapterStatus,
} from '../adapters/ledger-adapter-interface';
import { NullLedgerAdapter } from '../adapters/null-ledger-adapter';
import type {
  IScrtRuntimeAdapter,
  IScrtRuntimeAdapterRegistry,
  ScrtRuntimeAdapterStatus,
} from '../adapters/scrt-runtime-adapter-interface';
import { NullScrtRuntimeAdapter } from '../adapters/null-scrt-runtime-adapter';
import type {
  IStorageAdapter,
  IStorageAdapterRegistry,
  StorageAdapterStatus,
} from '../adapters/storage-adapter-interface';
import { NullStorageAdapter } from '../adapters/null-storage-adapter';
import type {
  IVmRuntimeAdapter,
  IVmRuntimeAdapterRegistry,
  VmRuntimeAdapterStatus,
} from '../adapters/vm-runtime-adapter-interface';
import { NullVmRuntimeAdapter } from '../adapters/null-vm-runtime-adapter';

export class LedgerAdapterRegistry implements ILedgerAdapterRegistry {
  private activeAdapter: ILedgerAdapter = new NullLedgerAdapter();
  private readonly adapters = new Map<string, ILedgerAdapter>();

  constructor() {
    this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
  }

  register(adapter: ILedgerAdapter): void {
    this.adapters.set(adapter.adapterId, adapter);
    if (adapter.getStatus() !== 'not_configured') {
      this.activeAdapter = adapter;
    }
  }

  unregister(adapterId: string): boolean {
    if (adapterId === 'null-ledger-adapter') return false;
    const removed = this.adapters.delete(adapterId);
    if (this.activeAdapter.adapterId === adapterId) {
      this.activeAdapter = new NullLedgerAdapter();
      this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
    }
    return removed;
  }

  getActiveAdapter(): ILedgerAdapter {
    return this.activeAdapter;
  }

  isConfigured(): boolean {
    return this.activeAdapter.getStatus() !== 'not_configured';
  }

  getStatus(): LedgerAdapterStatus {
    return this.activeAdapter.getStatus();
  }
}

export class StorageAdapterRegistry implements IStorageAdapterRegistry {
  private activeAdapter: IStorageAdapter = new NullStorageAdapter();
  private readonly adapters = new Map<string, IStorageAdapter>();

  constructor() {
    this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
  }

  register(adapter: IStorageAdapter): void {
    this.adapters.set(adapter.adapterId, adapter);
    if (adapter.getStatus() !== 'not_configured') {
      this.activeAdapter = adapter;
    }
  }

  unregister(adapterId: string): boolean {
    if (adapterId === 'null-storage-adapter') return false;
    const removed = this.adapters.delete(adapterId);
    if (this.activeAdapter.adapterId === adapterId) {
      this.activeAdapter = new NullStorageAdapter();
      this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
    }
    return removed;
  }

  getActiveAdapter(): IStorageAdapter {
    return this.activeAdapter;
  }

  isConfigured(): boolean {
    return this.activeAdapter.getStatus() !== 'not_configured';
  }

  getStatus(): StorageAdapterStatus {
    return this.activeAdapter.getStatus();
  }
}

export class ScrtRuntimeAdapterRegistry implements IScrtRuntimeAdapterRegistry {
  private activeAdapter: IScrtRuntimeAdapter = new NullScrtRuntimeAdapter();
  private readonly adapters = new Map<string, IScrtRuntimeAdapter>();

  constructor() {
    this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
  }

  register(adapter: IScrtRuntimeAdapter): void {
    this.adapters.set(adapter.adapterId, adapter);
    if (adapter.getStatus() !== 'not_configured') {
      this.activeAdapter = adapter;
    }
  }

  unregister(adapterId: string): boolean {
    if (adapterId === 'null-scrt-runtime-adapter') return false;
    const removed = this.adapters.delete(adapterId);
    if (this.activeAdapter.adapterId === adapterId) {
      this.activeAdapter = new NullScrtRuntimeAdapter();
      this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
    }
    return removed;
  }

  getActiveAdapter(): IScrtRuntimeAdapter {
    return this.activeAdapter;
  }

  isConfigured(): boolean {
    return this.activeAdapter.getStatus() !== 'not_configured';
  }

  getStatus(): ScrtRuntimeAdapterStatus {
    return this.activeAdapter.getStatus();
  }
}

export class VmRuntimeAdapterRegistry implements IVmRuntimeAdapterRegistry {
  private activeAdapter: IVmRuntimeAdapter = new NullVmRuntimeAdapter();
  private readonly adapters = new Map<string, IVmRuntimeAdapter>();

  constructor() {
    this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
  }

  register(adapter: IVmRuntimeAdapter): void {
    this.adapters.set(adapter.adapterId, adapter);
    if (adapter.getStatus() !== 'not_configured') {
      this.activeAdapter = adapter;
    }
  }

  unregister(adapterId: string): boolean {
    if (adapterId === 'null-vm-runtime-adapter') return false;
    const removed = this.adapters.delete(adapterId);
    if (this.activeAdapter.adapterId === adapterId) {
      this.activeAdapter = new NullVmRuntimeAdapter();
      this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
    }
    return removed;
  }

  getActiveAdapter(): IVmRuntimeAdapter {
    return this.activeAdapter;
  }

  isConfigured(): boolean {
    return this.activeAdapter.getStatus() !== 'not_configured';
  }

  getStatus(): VmRuntimeAdapterStatus {
    return this.activeAdapter.getStatus();
  }
}

export class CryptographyAdapterRegistry implements ICryptographyAdapterRegistry {
  private activeAdapter: ICryptographyAdapter = new NullCryptographyAdapter();
  private readonly adapters = new Map<string, ICryptographyAdapter>();

  constructor() {
    this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
  }

  register(adapter: ICryptographyAdapter): void {
    this.adapters.set(adapter.adapterId, adapter);
    if (adapter.getStatus() !== 'not_configured') {
      this.activeAdapter = adapter;
    }
  }

  unregister(adapterId: string): boolean {
    if (adapterId === 'null-cryptography-adapter') return false;
    const removed = this.adapters.delete(adapterId);
    if (this.activeAdapter.adapterId === adapterId) {
      this.activeAdapter = new NullCryptographyAdapter();
      this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
    }
    return removed;
  }

  getActiveAdapter(): ICryptographyAdapter {
    return this.activeAdapter;
  }

  isConfigured(): boolean {
    return this.activeAdapter.getStatus() !== 'not_configured';
  }

  getStatus(): CryptographyAdapterStatus {
    return this.activeAdapter.getStatus();
  }
}

export class EventBusAdapterRegistry implements IEventBusAdapterRegistry {
  private activeAdapter: IEventBusAdapter = new NullEventBusAdapter();
  private readonly adapters = new Map<string, IEventBusAdapter>();

  constructor() {
    this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
  }

  register(adapter: IEventBusAdapter): void {
    this.adapters.set(adapter.adapterId, adapter);
    if (adapter.getStatus() !== 'not_configured') {
      this.activeAdapter = adapter;
    }
  }

  unregister(adapterId: string): boolean {
    if (adapterId === 'null-event-bus-adapter') return false;
    const removed = this.adapters.delete(adapterId);
    if (this.activeAdapter.adapterId === adapterId) {
      this.activeAdapter = new NullEventBusAdapter();
      this.adapters.set(this.activeAdapter.adapterId, this.activeAdapter);
    }
    return removed;
  }

  getActiveAdapter(): IEventBusAdapter {
    return this.activeAdapter;
  }

  isConfigured(): boolean {
    return this.activeAdapter.getStatus() !== 'not_configured';
  }

  getStatus(): EventBusAdapterStatus {
    return this.activeAdapter.getStatus();
  }
}

export const ledgerAdapterRegistry = new LedgerAdapterRegistry();
export const storageAdapterRegistry = new StorageAdapterRegistry();
export const scrtRuntimeAdapterRegistry = new ScrtRuntimeAdapterRegistry();
export const vmRuntimeAdapterRegistry = new VmRuntimeAdapterRegistry();
export const cryptographyAdapterRegistry = new CryptographyAdapterRegistry();
export const eventBusAdapterRegistry = new EventBusAdapterRegistry();
